import { isPublicPath, type RouteTable } from "@hakili/app-shell";

/**
 * `P0-UIK-014`, and `WF-CON-0`: the five views, their screen sets and their
 * landing screens.
 *
 * **This table is canon's, copied and not designed.** 03, 2.0 gives it
 * literally , five views, each with a fixed screen set, the landing screen
 * being *"the first screen of its list"* , and the whole value of the item is
 * that the app disagrees with it nowhere. Anything invented here is a screen
 * somebody can reach that a founder never granted, or one they were granted
 * and cannot reach, and neither announces itself.
 *
 * **Danger sentence.** `WF-CON-0` says the console has NO login screen and no
 * view chooser in production: *"derive the view from the user's role"*, the
 * `Vue` switcher existing only behind the `demo` flag (`P0-UIK-018`). So the
 * view is not a preference, a setting or a URL parameter , it is a fact about
 * the caller, and a shell that let it be chosen would let a `chargé de
 * dossier` read the accounts screen by typing a query string.
 *
 * This is the client-side half of a two-sided rule and never the whole of it.
 * 6.1.8's four layers run on the server and are what actually refuse; the
 * whitelist spares a user a screen that would only have shown them a `403`.
 */

/** The five views of 03, 2.0. The ids are canon's own. */
export const VUES = ["admin", "resp", "collecte", "banques", "concess"] as const;

export type Vue = (typeof VUES)[number];

/**
 * The eleven screens of 03, 2.0, as stable ids. A whitelist names these and
 * never a path: paths change, and a whitelist keyed on one silently opens or
 * closes a screen when it does.
 */
export const ECRANS = [
  "collecte",
  "arrivees",
  "priorites",
  "partenaires",
  "developpement",
  "expertises",
  "equipe",
  "comptes",
  "supervision",
  "fonctionnalites",
  "profil",
] as const;

export type Ecran = (typeof ECRANS)[number];

export interface VueDefinition {
  /**
   * The catalogue KEY, never the words.
   *
   * 6.5.1 puts every French string in `packages/i18n`, and
   * `hakili/no-french-outside-catalogue` enforces it. The reason is
   * correctability, not tidiness: the founder will want to rename a view the
   * week before Bamako, and a label written here is a label written in five
   * more places by the time anyone asks.
   */
  readonly labelKey: string;
  /** In canon's order. The FIRST is the landing screen, by 03, 2.0's own rule. */
  readonly ecrans: readonly Ecran[];
}

export const VUE_DEFINITIONS: Readonly<Record<Vue, VueDefinition>> = {
  admin: {
    labelKey: "console.vue.admin",
    ecrans: [
      "collecte",
      "arrivees",
      "priorites",
      "partenaires",
      "developpement",
      "expertises",
      "equipe",
      "comptes",
      "supervision",
      "fonctionnalites",
      "profil",
    ],
  },
  resp: {
    labelKey: "console.vue.resp",
    // Canon writes "partenaires (assureurs only)" for this view. That is a
    // SCOPE inside the screen, not a different screen, and it is the server's
    // to enforce (6.1.8's third layer answers `out_of_scope`). The whitelist
    // grants the screen; what it lists once inside is not the shell's business.
    ecrans: ["collecte", "arrivees", "partenaires", "expertises", "equipe", "profil"],
  },
  collecte: { labelKey: "console.vue.collecte", ecrans: ["collecte", "profil"] },
  banques: {
    labelKey: "console.vue.banques",
    ecrans: ["priorites", "partenaires", "developpement", "profil"],
  },
  concess: {
    labelKey: "console.vue.concess",
    ecrans: ["priorites", "partenaires", "developpement", "profil"],
  },
};

/** The landing screen: 03, 2.0's "first screen of its list", derived and never restated. */
export function ecranDAtterrissage(vue: Vue): Ecran {
  const [premier] = VUE_DEFINITIONS[vue].ecrans;
  if (premier === undefined) {
    // A view with no screens lands nowhere and every URL then redirects to a
    // path that does not exist, which is an infinite redirect. Refused where a
    // developer is looking, in English: this is machinery talking to whoever
    // broke it, not a sentence a user reads.
    throw new Error(`view "${vue}" has no screens, so it lands nowhere`);
  }
  return premier;
}

export const ecransDeLaVue = (vue: Vue): ReadonlySet<Ecran> => new Set(VUE_DEFINITIONS[vue].ecrans);

/**
 * The routes of 4.6.4, each tagged with the screen it belongs to.
 *
 * A detail route belongs to its parent screen , `/dossiers/:id` is the
 * `collecte` screen looking at one file , so a view granted the list is
 * granted the file, and one denied the list cannot reach a file by its id.
 * Splitting them would let a `relation banques` user open a dossier by URL
 * while the list that would have shown it stays hidden.
 */
export const ROUTES: RouteTable = {
  landingScreen: "collecte",
  routes: [
    { path: "/dossiers", screen: "collecte" },
    { path: "/dossiers/:id", screen: "collecte" },
    // `WF-CON-11-NEW-BANK-PIPELINE`, `M1`: the chargé's recommendations , a detail of `collecte`, like the file.
    { path: "/recommandations", screen: "collecte" },
    { path: "/arrivees", screen: "arrivees" },
    { path: "/priorites", screen: "priorites" },
    { path: "/partenaires", screen: "partenaires" },
    { path: "/partenaires/:orgId", screen: "partenaires" },
    { path: "/developpement", screen: "developpement" },
    { path: "/expertises", screen: "expertises" },
    { path: "/expertises/:journeeId", screen: "expertises" },
    { path: "/equipe", screen: "equipe" },
    { path: "/comptes", screen: "comptes" },
    { path: "/supervision", screen: "supervision" },
    { path: "/fonctionnalites", screen: "fonctionnalites" },
    { path: "/profil", screen: "profil" },
    // `P0-CNW-027`, `00-SPINE` 6.3. A PUBLIC route: Hakili's own staff are
    // invited through the same door, their link is the console's, and the
    // token IS the authorisation , so it belongs to no view's whitelist and
    // `resolveRoute` renders it for anyone. It is last because `navigation()`
    // takes the first route of each screen and a public route is never a rail
    // entry, but a reader should not have to know that to place it.
    { path: "/invitation/:token", screen: "invitation", public: true },
  ],
};

/** The route table as a given view sees it: same routes, that view's landing. */
/**
 * Whether a path is served without a session, asked of the PATH alone.
 *
 * `P0-UIK-024`. The whitelist comes from the role and the role comes from
 * `/me`, so an application cannot use `resolveRoute` to decide whether to CALL
 * `/me`. A public route's authorisation is a property of the table, so this
 * question has an answer before anybody knows who is asking.
 */
export const cheminPublic = (path: string): boolean => isPublicPath(ROUTES, path);

export const routesPourLaVue = (vue: Vue): RouteTable => ({
  ...ROUTES,
  landingScreen: ecranDAtterrissage(vue),
});

/**
 * The PATH a view lands on, derived from the table rather than restated.
 *
 * `collecte` is served at `/dossiers`, and that mapping is written once, in
 * `ROUTES`. A second copy , even a three-character ternary , is a second
 * answer to "where does a chargé de dossier start", and the two would diverge
 * the first time a path changed.
 */
export function cheminDAtterrissage(vue: Vue): string {
  const ecran = ecranDAtterrissage(vue);
  const route = ROUTES.routes.find((candidate) => candidate.screen === ecran);
  if (route === undefined) throw new Error(`no route serves the landing screen "${ecran}"`);
  return route.path;
}
